import { MoreVertical } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ActionButton } from '@/shared/ui/action-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { DataTableRow, TableAction } from '../types';

interface RowActionsProps<T extends DataTableRow> {
  actions: TableAction<T>[];
  row: T;
}

export function RowActions<T extends DataTableRow>({ actions, row }: RowActionsProps<T>) {
  // Resolve per-row gating: hidden actions are dropped, disabled ones stay but can't be clicked.
  const visibleActions = actions.filter((action) => !action.isHidden?.(row));

  if (visibleActions.length === 0) return null;

  return (
    <div className="flex items-center gap-1">
      {visibleActions.slice(0, 2).map((action) => (
        <Tooltip key={action.id}>
          <TooltipTrigger asChild>
            <ActionButton
              type="button"
              intent={action.intent ?? 'view'}
              size="icon"
              className="h-9 w-9"
              aria-label={action.label}
              disabled={action.isDisabled?.(row)}
              onClick={() => action.onClick?.(row)}>
              {action.icon && <action.icon className="w-4 h-4" />}
            </ActionButton>
          </TooltipTrigger>
          <TooltipContent>{action.label}</TooltipContent>
        </Tooltip>
      ))}

      {visibleActions.length > 2 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {visibleActions.slice(2).map((action) => (
              <DropdownMenuItem
                key={action.id}
                disabled={action.isDisabled?.(row)}
                onClick={() => action.onClick?.(row)}>
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
